import FlashPCBSelect from "../common/FlashPCBSelect";
import React, { useCallback, useEffect, useRef, useState } from "react";
import settings from "../../constants/settings";
import useFetchAndTrace from "../../utils/hooks/fetchWithTrace";
import { useDebouncedCallback } from "use-debounce";
import { v4 as uuidv4 } from "uuid";
import { useUser } from "../../utils/hooks/commonly-stored/users";
import { UserSummary } from "../../reducers/models/users";

interface UserOptionType {
  label: string;
  value: UserSummary;
}

interface SelectUserProps {
  selectedUserId: string | null;
  setSelectedUser: (user: UserSummary | null) => void;
  isClearable: boolean;
  label?: string;
}

export default function SelectUser({
  selectedUserId,
  setSelectedUser,
  isClearable,
  label,
}: SelectUserProps) {
  const fetchAndTrace = useFetchAndTrace();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<UserSummary[]>([]);
  const [nextPageToFetch, setNextPageToFetch] = useState(1);
  const [inputValue, setInputValue] = useState("");
  const { user } = useUser(selectedUserId);
  const getUsersRequestIdRef = useRef<string>(uuidv4());
  const [menuIsOpen, setMenuIsOpen] = useState(false);

  const getLoadUsersHandler = useCallback((requestId: string) => {
    return async (response: Response) => {
      if (requestId == getUsersRequestIdRef.current) {
        if (response.ok) {
          const usersResponse = (await response.json()) as {
            users: UserSummary[];
          };
          setUsers((prevState) => {
            const existingUserIds = new Set(prevState.map((user) => user.id));
            return [
              ...prevState,
              ...usersResponse.users.filter(
                (user) => !existingUserIds.has(user.id),
              ),
            ];
          });
          setNextPageToFetch((prevPage) => prevPage + 1);
          setLoading(false);
        } else {
          throw Error("Failed to get all boards");
        }
      }
    };
  }, []);

  const loadMoreUsers = useDebouncedCallback((requestId: string) => {
    setLoading(true);
    void fetchAndTrace(
      `${settings.api_url}/users/get-users`,
      getLoadUsersHandler(requestId),
      null,
      {
        body: JSON.stringify({
          page: nextPageToFetch,
          email: inputValue,
        }),
        method: "PUT",
      },
    );
  }, 300);

  function onSelectUser(newValue: UserOptionType | null) {
    setUsers([]);
    setSelectedUser(newValue?.value || null);
  }

  useEffect(() => {
    getUsersRequestIdRef.current = uuidv4();
    setLoading(true);
    if (!inputValue) {
      setUsers([]);
    }
    setNextPageToFetch(1);
    loadMoreUsers(getUsersRequestIdRef.current);
  }, [inputValue, loadMoreUsers, selectedUserId]);

  const getValue = () => {
    // In the case that the selected id is null we return null so that the field is cleared
    if (selectedUserId === null) {
      return null;
    }
    // Otherwise, we allow undefined which allows for a less jumpy experience as the userResponse loads
    return (
      user && {
        label: user.email,
        value: user,
      }
    );
  };

  return (
    <FlashPCBSelect
      label={label || "Users"}
      name="Users"
      isLoading={
        (loading && menuIsOpen) ||
        (selectedUserId !== null && selectedUserId !== user?.id)
      }
      onMenuClose={() => setMenuIsOpen(false)}
      onMenuOpen={() => setMenuIsOpen(true)}
      loadingMessage={() => "Searching users..."}
      placeholder={"Enter email"}
      isClearable={isClearable}
      inputValue={inputValue}
      onInputChange={setInputValue}
      value={getValue()}
      options={users?.map((user) => {
        return {
          label: user.email,
          value: user,
        };
      })}
      noOptionsMessage={() =>
        inputValue ? "No users found" : "Search by email address"
      }
      onMenuScrollToBottom={() => loadMoreUsers(getUsersRequestIdRef.current)}
      onChange={onSelectUser}
    />
  );
}
